import React from "react";
import { EyeIcon, ChatCircleIcon, ArrowRightIcon } from "@phosphor-icons/react";
import { useGo, useList, useTranslate } from "@refinedev/core";
import { ANALYTICS_MAX_DATE_RANGE_MONTHS } from "@/constants";
import dayjs from "dayjs";
import type { SessionStatistics } from "types/";
import {
  DSection,
  DSectionLabel,
  StatGridD,
  StatCardD,
  StatNumberD,
  StatLabelD,
  AnalyticsLinkD,
} from "./drawer-styled";

type AnalyticsMiniSectionProps = {
  projectId: string;
  creationDate: string;
};

export const AnalyticsMiniSection: React.FC<AnalyticsMiniSectionProps> = ({
  projectId,
  creationDate,
}) => {
  const t = useTranslate();
  const go = useGo();

  const minDate = dayjs().subtract(ANALYTICS_MAX_DATE_RANGE_MONTHS, "month");
  const projectCreationDate = dayjs(creationDate);
  const startDate = (
    projectCreationDate.isAfter(minDate) ? projectCreationDate : minDate
  ).format("YYYY-MM-DD");
  const endDate = dayjs().format("YYYY-MM-DD");

  const { data, isFetching } = useList<SessionStatistics>({
    resource: `analytics/projects/${projectId}/sessions/v2`,
    config: {
      filters: [
        { field: "from_date", operator: "eq", value: startDate },
        { field: "to_date", operator: "eq", value: endDate },
      ],
    },
    queryOptions: {
      enabled: !!projectId && !!creationDate,
      retry: false,
      staleTime: 1000 * 60 * 5,
    },
    errorNotification: false,
  });

  const stats = data?.data?.[0];
  const views = stats?.sessions ?? 0;
  const chatInteractions = stats?.chat_interactions ?? 0;

  const formatNumber = (n: number) =>
    n >= 1000 ? `${(n / 1000).toFixed(1).replace(/\.0$/, "")}k` : String(n);

  return (
    <DSection>
      <DSectionLabel>
        {t("media.components.ProjectSettingsDrawer.analytics", "Analytics")}
      </DSectionLabel>
      <StatGridD>
        <StatCardD>
          <StatNumberD>{isFetching ? "—" : formatNumber(views)}</StatNumberD>
          <StatLabelD>
            <EyeIcon size={14} color="#14B8A6" />
            {t("media.components.ProjectSettingsDrawer.views", "Views")}
          </StatLabelD>
        </StatCardD>
        <StatCardD>
          <StatNumberD>
            {isFetching ? "—" : formatNumber(chatInteractions)}
          </StatNumberD>
          <StatLabelD>
            <ChatCircleIcon size={14} color="#14B8A6" />
            {t(
              "media.components.ProjectSettingsDrawer.chatInteractions",
              "Chat interactions"
            )}
          </StatLabelD>
        </StatCardD>
      </StatGridD>
      <AnalyticsLinkD
        onClick={() =>
          go({ to: `/media/projects/${projectId}/analytics?period=all` })
        }
      >
        {t(
          "media.components.ProjectSettingsDrawer.exploreAnalytics",
          "Explore full analytics"
        )}
        <ArrowRightIcon size={13} />
      </AnalyticsLinkD>
    </DSection>
  );
};
